// =============================================================================
// Image URL Utility - Supports Persistent Storage
// =============================================================================
// This utility handles image URLs for the blog system.
// Blog images are stored in persistent storage (/app/uploads/blog/)
// and served via /api/uploads/blog/ endpoint.
//
// Supported URL formats:
// 1. /api/uploads/blog/xxx - new persistent storage (correct format)
// 2. /uploads/blog/xxx - old format, converted to /api/uploads/blog/
// 3. /uploads/xxx - legacy general uploads
// 4. http(s)://... - external URLs (returned as-is)
// =============================================================================

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // If it's an external URL (http/https), return as is
  if (imageUrl.startsWith('http')) {
    return imageUrl;
  }
  
  let cleanPath = imageUrl;
  
  // Handle different path formats
  if (imageUrl.startsWith('/api/')) {
    // Already has /api prefix (new format) - use as-is
    cleanPath = imageUrl;
  } else if (imageUrl.startsWith('/uploads/')) {
    // Convert old /uploads/ to /api/uploads/ for Kubernetes routing
    cleanPath = imageUrl.replace('/uploads/', '/api/uploads/');
  } else if (!imageUrl.startsWith('/')) {
    // Path doesn't start with /, add /api/ prefix
    cleanPath = `/api/${imageUrl}`;
  } else {
    // Other paths, add /api prefix
    cleanPath = `/api${imageUrl}`;
  }
  
  return `${API_BASE}${cleanPath}`;
};

// Helper to check if image URL is from persistent storage
export const isPersistentStorageUrl = (imageUrl) => {
  if (!imageUrl) return false;
  return imageUrl.includes('/api/uploads/blog/') || imageUrl.includes('/uploads/blog/');
};

// Helper to get a placeholder image
export const getPlaceholderImage = (width = 800, height = 600, text = 'No Image') => {
  return `https://via.placeholder.com/${width}x${height}?text=${encodeURIComponent(text)}`;
};

// Helper to construct blog image URL from filename
export const getBlogImageUrl = (filename) => {
  if (!filename) return null;
  if (filename.startsWith('http')) return filename;
  
  // Ensure filename is just the filename, not a full path
  const cleanFilename = filename.split('/').pop();
  return `${API_BASE}/api/uploads/blog/${cleanFilename}`;
};

export default getImageUrl;
